<template>
	<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-6">
			<label for="name">{{$t('Squeeze.DocumentClasses.Name')}} *</label>
			<InputText id="name" v-model="v$.name.$model" :class="{'p-invalid':v$.name.$invalid && showErrorMessage}" @change="update" required="true" autofocus />
			<small v-if="v$.name.required.$invalid && showErrorMessage && !v$.name.technicalValidation.$invalid" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Name')}) }}
			</small>
			<small v-if="v$.name.technicalValidation.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.OnlyAllowAlphaNumericUnderscore', { field: $t('Squeeze.DocumentClasses.Name')}) }}
			</small>
			<small v-else-if="!!value.id">
				{{ $t('Squeeze.Admin.WarnRename') }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label for="description">{{$t('Squeeze.DocumentClasses.Description')}} *</label>
			<DexInputText id="description" v-model="v$.description.$model" :translatedDescription="value.translatedDescription" :translationKey="value.translationKey" :class="{'p-invalid':v$.description.$invalid && showErrorMessage}" @change="update" required="true" />
			<small v-if="(v$.description.$invalid && showErrorMessage)" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Description')}) }}
			</small>
		</div>
		<!-- Only active with active FeatureFlag "llmExtraction" -->
		<div v-if="store.state.featureSet.llmExtraction" class="p-field p-col-12 p-d-flex p-ai-center">
			<Checkbox id="llmExtraction" v-model="llmActiveLocal" :binary="true" @change="onChangeLlmActive" />
			<label for="llmExtraction" class="p-ml-2 p-mb-0">{{$t('Squeeze.DocumentClasses.LLMActive')}}</label>
		</div>
	</div>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, reactive, ref, watch} from "vue";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import DexInputText from "@/components/DexInputText.vue";
import {DocumentClass} from "@dex/squeeze-client-ts";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import {technicalValidation} from "@/util/ValidationHelper";
import {useSqueezeStore} from "@/apps/squeeze/store";
import {FormState} from "@/util/Types";

type DocumentClassFormState = FormState<DocumentClass, "name" | "description">;

export default defineComponent({
	name: "DocumentClassForm",
	components: {
		InputText,
		DexInputText,
		Checkbox,
	},
	props: {
		documentClass: {
			type: Object as PropType<DocumentClass>,
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
			default: false,
		},
		llmActive: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update', 'update:llmActive'],
	setup(props, {emit}) {
		/** Current Vuex-Store */
		const store = useSqueezeStore();

		/** Current Object of all input-fields */
		const value = reactive<DocumentClassFormState>({name: '', description: ''});

		/** Local mirror of the llmActive prop for the "LLM active" checkbox */
		const llmActiveLocal = ref<boolean>(props.llmActive);

		/** Keep the local checkbox state in sync when the parent updates the setting value */
		watch(() => props.llmActive, newValue => {
			llmActiveLocal.value = newValue;
		});

		/** Emit the checkbox change so the parent view can persist the LLMExtraction setting */
		const onChangeLlmActive = () => {
			emit("update:llmActive", llmActiveLocal.value);
		}

		/** Determines the required rules for validation */
		const rules = {
			name: { required, technicalValidation },
			description: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		onMounted(async () => {
			Object.assign(value, props.documentClass);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.documentClass, async () => {
			Object.assign(value, props.documentClass);

			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		})

		/** Triggered when keyup in input-field */
		const update = () => {
			emit("update", value, v$.value.$invalid);
		}

		return {
			store,
			value,
			llmActiveLocal,
			onChangeLlmActive,
			props,
			v$,
			InputText,
			update,
		}
	},
});
</script>

<style scoped>

</style>
